import React, { useState, useRef, useEffect } from 'react';
import QRCode from 'qrcode';
import jsPDF from 'jspdf';
import './QRDownload.css';

const QRDownload = ({ data, settings, customization, logo, qrType, inputData }) => {
  const [downloadFormat, setDownloadFormat] = useState('png');
  const [downloadSize, setDownloadSize] = useState(512);
  const canvasRef = useRef(null);

  const generateFileName = () => {
    let baseName = 'qr-code';

    try {
      switch (qrType) {
        case 'url':
          if (inputData && typeof inputData === 'string') {
            try {
              const url = new URL(inputData);
              baseName = url.hostname.replace(/^www\./, '');
            } catch {
              baseName = 'url-qr';
            }
          }
          break;
        case 'text':
          if (inputData && typeof inputData === 'string') {
            const firstWord = inputData.trim().split(/\s+/)[0];
            baseName = firstWord.substring(0, 20).replace(/[^a-zA-Z0-9-_]/g, '_');
          }
          break;
        case 'wifi':
          if (inputData && inputData.ssid) {
            baseName = inputData.ssid.replace(/[^a-zA-Z0-9-_]/g, '_');
          }
          break;
        case 'vcard':
          if (inputData && inputData.name) {
            baseName = inputData.name.replace(/[^a-zA-Z0-9-_]/g, '_');
          }
          break;
        default:
          baseName = 'qr-code';
      }

      if (!baseName || baseName === '') {
        baseName = 'qr-code';
      }
    } catch {
      baseName = 'qr-code';
    }

    return baseName;
  };

  useEffect(() => {
    if (!data) return;
    generateQRForDownload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, settings, customization, logo, downloadSize]);

  const generateQRForDownload = async () => {
    if (!canvasRef.current || !data) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    canvas.width = downloadSize;
    canvas.height = downloadSize;

    const qrOptions = {
      errorCorrectionLevel: logo ? 'H' : settings.errorCorrectionLevel,
      margin: settings.margin,
      width: downloadSize,
      color: {
        dark: customization.fgColor,
        light: customization.transparentBg ? '#00000000' : customization.bgColor
      }
    };

    if (settings.version !== 'auto') {
      qrOptions.version = parseInt(settings.version);
    }

    try {
      await QRCode.toCanvas(canvas, data, qrOptions);

      // Apply gradient if needed
      if (customization.gradientType !== 'none') {
        applyGradient(canvas, ctx);
      }

      // Add logo if present
      if (logo) {
        await addLogoToCanvas(canvas, ctx, logo);
      }
    } catch (error) {
      console.error('QR generation error:', error);
    }
  };

  const applyGradient = (canvas, ctx) => {
    let gradient;
    if (customization.gradientType === 'linear') {
      gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    } else {
      gradient = ctx.createRadialGradient(
        canvas.width / 2, canvas.height / 2, 0,
        canvas.width / 2, canvas.height / 2, canvas.width / 2
      );
    }
    gradient.addColorStop(0, customization.gradientColors[0]);
    gradient.addColorStop(1, customization.gradientColors[1]);

    ctx.globalCompositeOperation = 'source-in';
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.globalCompositeOperation = 'source-over';
  };

  const addLogoToCanvas = (canvas, ctx, logoSrc) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const maxLogoSize = canvas.width * 0.25;
        let logoWidth = img.width;
        let logoHeight = img.height;

        if (logoWidth > maxLogoSize || logoHeight > maxLogoSize) {
          const scale = Math.min(maxLogoSize / logoWidth, maxLogoSize / logoHeight);
          logoWidth *= scale;
          logoHeight *= scale;
        }

        const x = (canvas.width - logoWidth) / 2;
        const y = (canvas.height - logoHeight) / 2;

        const padding = 10;
        ctx.fillStyle = 'white';
        ctx.fillRect(x - padding, y - padding, logoWidth + padding * 2, logoHeight + padding * 2);

        ctx.drawImage(img, x, y, logoWidth, logoHeight);
        resolve();
      };
      img.src = logoSrc;
    });
  };

  const downloadPNG = () => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = `${generateFileName()}.png`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
    });
  };

  const downloadSVG = async () => {
    if (!data) return;

    try {
      const svgString = await QRCode.toString(data, {
        type: 'svg',
        errorCorrectionLevel: logo ? 'H' : settings.errorCorrectionLevel,
        margin: settings.margin,
        color: {
          dark: customization.fgColor,
          light: customization.transparentBg ? 'transparent' : customization.bgColor
        }
      });

      const blob = new Blob([svgString], { type: 'image/svg+xml' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = `${generateFileName()}.svg`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('SVG generation error:', error);
    }
  };

  const downloadPDF = () => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    const imgData = canvas.toDataURL('image/png');

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const qrSize = Math.min(pdfWidth, pdfHeight) * 0.6;
    const x = (pdfWidth - qrSize) / 2;
    const y = (pdfHeight - qrSize) / 2;

    pdf.addImage(imgData, 'PNG', x, y, qrSize, qrSize);
    pdf.save(`${generateFileName()}.pdf`);
  };

  const handleDownload = () => {
    if (!data) {
      alert('Please enter data to generate a QR code first');
      return;
    }

    switch (downloadFormat) {
      case 'png':
        downloadPNG();
        break;
      case 'svg':
        downloadSVG();
        break;
      case 'pdf':
        downloadPDF();
        break;
      default:
        downloadPNG();
    }
  };

  return (
    <div className="qr-download">
      <h3>Download QR Code</h3>

      <div className="download-options">
        <div className="option-group">
          <label>Format</label>
          <select value={downloadFormat} onChange={(e) => setDownloadFormat(e.target.value)}>
            <option value="png">PNG</option>
            <option value="svg">SVG</option>
            <option value="pdf">PDF (Print-Ready)</option>
          </select>
        </div>

        {downloadFormat === 'png' && (
          <div className="option-group">
            <label>Size (px)</label>
            <select value={downloadSize} onChange={(e) => setDownloadSize(parseInt(e.target.value))}>
              <option value="256">256 x 256</option>
              <option value="512">512 x 512</option>
              <option value="1024">1024 x 1024</option>
              <option value="2048">2048 x 2048</option>
              <option value="4096">4096 x 4096</option>
            </select>
          </div>
        )}
      </div>

      <button
        className="download-btn"
        onClick={handleDownload}
        disabled={!data}
      >
        📥 Download {downloadFormat.toUpperCase()}
      </button>

      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};

export default QRDownload;
